import React from 'react';
import { signupStyles } from '@/components/features/registration/signup-templates/signup.styles';
import { Box, Stack, Button, Field, Input } from '@chakra-ui/react';
import PhoneInput from 'react-phone-input-2';
import { clinicDetailsSchema, TClinicDetails } from './clinic-details.validator';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ReactFlagsSelect from 'react-flags-select';
import { TClinicDetailsProps } from '../../../templates/clinic/types/template.types';

const ClinicDetails = ({ next, details }: TClinicDetailsProps) => {
   const {
      register,
      handleSubmit,
      reset,
      setValue,
      watch,
      formState: { errors },
   } = useForm<TClinicDetails>({
      defaultValues: details,
      resolver: zodResolver(clinicDetailsSchema),
   });

   const onSubmit = handleSubmit(async data => {
      next('specialization', data);
   });

   const { phone_number, nationality } = watch();
   return (
      <Box as="form" css={signupStyles.signup_form} onSubmit={onSubmit}>
         <Stack gap="4" align="flex-start" minW="full">
            <Field.Root invalid={Boolean(errors.address_1?.message)}>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Address Line 1
                  <Box as="span" color="error">
                     *
                  </Box>
               </Field.Label>
               <Input
                  placeholder="Address Line 1"
                  variant="subtle"
                  h="10"
                  rounded="12px"
                  bg="#202020"
                  {...register('address_1')}
               />
               <Field.ErrorText>{errors.address_1?.message}</Field.ErrorText>
            </Field.Root>
            <Field.Root invalid={Boolean(errors.address_2?.message)}>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Address Line 2
               </Field.Label>
               <Input
                  placeholder="Address Line 2"
                  variant="subtle"
                  h="10"
                  rounded="12px"
                  bg="#202020"
                  {...register('address_2')}
               />
               <Field.ErrorText>{errors.address_2?.message}</Field.ErrorText>
            </Field.Root>
            <Box css={signupStyles.double_input}>
               <Field.Root invalid={Boolean(errors.post_code?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Post Code
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     placeholder="Post Code"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     {...register('post_code')}
                  />
                  <Field.ErrorText>{errors.post_code?.message}</Field.ErrorText>
               </Field.Root>

               <Field.Root invalid={Boolean(errors.phone_number?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Phone Number
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <PhoneInput
                     country={'gb'}
                     preferredCountries={['gb', 'us']}
                     placeholder="Phone Number"
                     enableSearch
                     value={phone_number}
                     onChange={v => setValue('phone_number', v)}
                     inputStyle={{
                        width: '100%',
                        height: '40px',
                        background: '#202020',
                        borderRadius: '12px',
                        border: errors.phone_number?.message ? '1px solid #f87171' : 'none',
                        color: 'white',
                        paddingLeft: '46px',
                        fontSize: '14px',
                     }}
                     dropdownStyle={{
                        backgroundColor: '#000',
                        color: 'white',
                     }}
                  />
                  <Field.ErrorText>{errors.phone_number?.message}</Field.ErrorText>
               </Field.Root>
            </Box>
            <Box css={signupStyles.double_input}>
               <Field.Root invalid={Boolean(errors.state?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Town
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     type="text"
                     css={signupStyles.num_input}
                     placeholder="Town"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     {...register('state')}
                  />
                  <Field.ErrorText>{errors.state?.message}</Field.ErrorText>
               </Field.Root>

               <Field.Root invalid={Boolean(errors.city?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     City
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     type="text"
                     css={signupStyles.num_input}
                     placeholder="City"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     {...register('city')}
                  />
                  <Field.ErrorText>{errors.city?.message}</Field.ErrorText>
               </Field.Root>
            </Box>
            <Field.Root invalid={Boolean(errors.nationality?.message)}>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Residence
                  <Box as="span" color="error">
                     *
                  </Box>
               </Field.Label>
               <Box
                  css={{
                     ...signupStyles.resident_input,
                     ...(errors.nationality?.message ? signupStyles.resident_input_error : {}),
                  }}
               >
                  <ReactFlagsSelect
                     selected={nationality}
                     onSelect={code => setValue('nationality', code)}
                     searchable
                     fullWidth
                     countries={['GB']}
                  />
               </Box>

               <Field.ErrorText>{errors.nationality?.message}</Field.ErrorText>
            </Field.Root>
            <Box css={signupStyles.signup_btn_con}>
               <Button
                  type="reset"
                  onClick={() => reset()}
                  bg="darkerBtn"
                  textStyle="xs"
                  color="white"
                  h="10"
                  maxW="36"
                  minW="36"
                  p="0"
               >
                  Reset
               </Button>
               <Button type="submit" textStyle="xs" h="10" maxW="36" minW="36" p="0">
                  Next
               </Button>
            </Box>
         </Stack>
      </Box>
   );
};

export default ClinicDetails;
